import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";

export type ConfirmDialogVariant = "primary" | "danger";

export interface ConfirmDialogCstProps {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  confirmVariant?: ConfirmDialogVariant;
  isSubmitting?: boolean;
  submittingLabel?: string;
}

const ConfirmDialogCst: React.FC<ConfirmDialogCstProps> = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onClose,
  onConfirm,
  confirmVariant = "primary",
  isSubmitting = false,
  submittingLabel,
}) => {
  const theme = useTheme();

  const confirmBg =
    confirmVariant === "danger"
      ? theme.palette.error.main
      : theme.palette.primary.main;
  const confirmBgHover =
    confirmVariant === "danger"
      ? theme.palette.error.dark
      : theme.palette.primary.dark;
  const confirmFg =
    confirmVariant === "danger"
      ? theme.palette.error.contrastText
      : theme.palette.primary.contrastText;

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px 8px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          disabled={isSubmitting}
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "8px 24px 16px" }}>
        {typeof description === "string" ? (
          <Typography
            sx={{
              fontSize: "14px",
              color: theme.palette.text.primary,
              lineHeight: 1.5,
            }}
          >
            {description}
          </Typography>
        ) : (
          description
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "8px 24px 20px", gap: 1 }}>
        <Button
          onClick={onClose}
          variant="text"
          disabled={isSubmitting}
          sx={{
            textTransform: "none",
            color: theme.palette.text.primary,
            fontWeight: 600,
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            backgroundColor: confirmBg,
            color: confirmFg,
            "&:hover": { backgroundColor: confirmBgHover },
          }}
        >
          {isSubmitting && submittingLabel ? submittingLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialogCst;
